import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import apiClient from '../api/client';

interface EmployeeStatus {
  id: string;
  name: string;
  email: string;
  assigned_cubicle: string | null;
  current_status: string;
  sedentary_time_minutes: number;
  avg_posture_score: number | null;
  alert_count: number;
}

interface AlertsByType {
  break_reminder: number;
  posture_correction: number;
  hydration_reminder: number;
  cold_detection: number;
}

interface AggregateMetrics {
  total_employees: number;
  active_employees: number;
  avg_posture_score: number | null;
  total_alerts_today: number;
  total_alerts_week: number;
  avg_sedentary_time_minutes: number;
  alerts_by_type_today: AlertsByType;
  alerts_by_type_week: AlertsByType;
}

export function AdminDashboard() {
  const [employees, setEmployees] = useState<EmployeeStatus[]>([]);
  const [aggregate, setAggregate] = useState<AggregateMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [empResponse, aggResponse] = await Promise.all([
        apiClient.get('/api/admin/employees'),
        apiClient.get('/api/admin/aggregate')
      ]);
      setEmployees(empResponse.data.employees);
      setAggregate(aggResponse.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return '#10B981';
      case 'on_break': return '#F59E0B';
      case 'offline': return '#9CA3AF';
      default: return '#6B7280';
    }
  };

  const getPostureColor = (score: number | null): string => {
    if (score === null) return '#9CA3AF';
    if (score >= 70) return '#10B981';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
  };

  const getPostureGrade = (score: number | null): string => {
    if (score === null) return '--';
    if (score >= 80) return 'A';
    if (score >= 70) return 'B';
    if (score >= 60) return 'C';
    if (score >= 50) return 'D';
    return 'F';
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <p style={styles.loadingText}>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <span style={styles.errorIcon}>⚠️</span>
        <h3>Failed to load data</h3>
        <p>{error}</p>
        <button onClick={fetchData} style={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  const alertsChartData = aggregate ? [
    { name: 'Break', value: aggregate.alerts_by_type_today?.break_reminder ?? 0, color: '#F59E0B' },
    { name: 'Posture', value: aggregate.alerts_by_type_today?.posture_correction ?? 0, color: '#3B82F6' },
    { name: 'Hydration', value: aggregate.alerts_by_type_today?.hydration_reminder ?? 0, color: '#06B6D4' },
    { name: 'Cold', value: aggregate.alerts_by_type_today?.cold_detection ?? 0, color: '#8B5CF6' },
  ] : [];

  return (
    <div style={styles.container}>
      {/* KPI Cards Row */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCard}>
          <div style={styles.kpiIconWrapper}>
            <span style={styles.kpiIcon}>👥</span>
          </div>
          <div style={styles.kpiContent}>
            <span style={styles.kpiValue}>{aggregate?.total_employees ?? 0}</span>
            <span style={styles.kpiLabel}>Total Employees</span>
          </div>
          <div style={styles.kpiTrend}>
            <span style={styles.kpiTrendBadge}>Registered</span>
          </div>
        </div>

        <div style={{...styles.kpiCard, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)'}}>
          <div style={{...styles.kpiIconWrapper, background: 'rgba(255,255,255,0.2)'}}>
            <span style={styles.kpiIcon}>🟢</span>
          </div>
          <div style={styles.kpiContent}>
            <span style={{...styles.kpiValue, color: 'white'}}>{aggregate?.active_employees ?? 0}</span>
            <span style={{...styles.kpiLabel, color: 'rgba(255,255,255,0.8)'}}>Active Now</span>
          </div>
          <div style={styles.kpiTrend}>
            <span style={{...styles.kpiTrendBadge, background: 'rgba(255,255,255,0.2)', color: 'white'}}>
              {aggregate?.total_employees ? Math.round((aggregate.active_employees / aggregate.total_employees) * 100) : 0}% online
            </span>
          </div>
        </div>

        <div style={styles.kpiCard}>
          <div style={{...styles.kpiIconWrapper, background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'}}>
            <span style={styles.kpiIcon}>🧘</span>
          </div>
          <div style={styles.kpiContent}>
            <span style={styles.kpiValue}>{aggregate?.avg_posture_score?.toFixed(0) ?? '--'}</span>
            <span style={styles.kpiLabel}>Avg Posture Score</span>
          </div>
          <div style={styles.kpiTrend}>
            <span style={{
              ...styles.kpiTrendBadge,
              background: getPostureColor(aggregate?.avg_posture_score ?? null) + '20',
              color: getPostureColor(aggregate?.avg_posture_score ?? null),
            }}>
              Grade {getPostureGrade(aggregate?.avg_posture_score ?? null)}
            </span>
          </div>
        </div>

        <div style={styles.kpiCard}>
          <div style={{...styles.kpiIconWrapper, background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'}}>
            <span style={styles.kpiIcon}>⏱️</span>
          </div>
          <div style={styles.kpiContent}>
            <span style={styles.kpiValue}>{aggregate?.avg_sedentary_time_minutes?.toFixed(0) ?? 0}m</span>
            <span style={styles.kpiLabel}>Avg Sedentary</span>
          </div>
        </div>

        <div style={styles.kpiCard}>
          <div style={{...styles.kpiIconWrapper, background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'}}>
            <span style={styles.kpiIcon}>🔔</span>
          </div>
          <div style={styles.kpiContent}>
            <span style={styles.kpiValue}>{aggregate?.total_alerts_today ?? 0}</span>
            <span style={styles.kpiLabel}>Alerts Today</span>
          </div>
        </div>

        <div style={styles.kpiCard}>
          <div style={{...styles.kpiIconWrapper, background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'}}>
            <span style={styles.kpiIcon}>📊</span>
          </div>
          <div style={styles.kpiContent}>
            <span style={styles.kpiValue}>{aggregate?.total_alerts_week ?? 0}</span>
            <span style={styles.kpiLabel}>Alerts This Week</span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div style={styles.chartsRow}>
        {/* Alerts Distribution */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Today's Alert Distribution</h3>
          <div style={styles.chartContent}>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={alertsChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {alertsChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div style={styles.chartLegend}>
              {alertsChartData.map((item, idx) => (
                <div key={idx} style={styles.legendItem}>
                  <div style={{...styles.legendDot, background: item.color}} />
                  <span style={styles.legendLabel}>{item.name}</span>
                  <span style={styles.legendValue}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Alert Types Bar Chart */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Alert Types Comparison</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={alertsChartData} layout="vertical">
              <XAxis type="number" stroke="#9CA3AF" fontSize={12} />
              <YAxis type="category" dataKey="name" stroke="#9CA3AF" fontSize={12} width={70} />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: 'none', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                }} 
              />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {alertsChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Employee Table */}
      <div style={styles.tableSection}>
        <div style={styles.tableHeader}>
          <h3 style={styles.tableTitle}>Employee Overview</h3>
          <span style={styles.tableCount}>{employees.length} employees</span>
        </div>
        
        {employees.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>👥</span>
            <p>No employees found</p>
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Employee</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Cubicle</th>
                  <th style={styles.th}>Posture</th>
                  <th style={styles.th}>Sedentary</th>
                  <th style={styles.th}>Alerts</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.id} style={styles.tr} onClick={() => navigate(`/admin/employee/${emp.id}`)}>
                    <td style={styles.td}>
                      <div style={styles.employeeCell}>
                        <div style={styles.employeeAvatar}>
                          {emp.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={styles.employeeName}>{emp.name}</div>
                          <div style={styles.employeeEmail}>{emp.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        background: getStatusColor(emp.current_status) + '15',
                        color: getStatusColor(emp.current_status),
                      }}>
                        <span style={{
                          ...styles.statusDot,
                          background: getStatusColor(emp.current_status),
                        }} />
                        {emp.current_status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.cubicleTag}>{emp.assigned_cubicle || '—'}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.postureCell}>
                        <span style={{
                          ...styles.postureScore,
                          color: getPostureColor(emp.avg_posture_score),
                        }}>
                          {emp.avg_posture_score?.toFixed(0) ?? '--'}
                        </span>
                        <div style={styles.postureBar}>
                          <div style={{
                            ...styles.postureBarFill,
                            width: `${emp.avg_posture_score ?? 0}%`,
                            background: getPostureColor(emp.avg_posture_score),
                          }} />
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.sedentaryValue}>{emp.sedentary_time_minutes}m</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.alertBadge}>{emp.alert_count}</span>
                    </td>
                    <td style={styles.td}>
                      <button style={styles.viewBtn}>View →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


const styles: { [key: string]: React.CSSProperties } = {
  container: { maxWidth: '1400px', margin: '0 auto' },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: '20px',
  },
  loadingSpinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #E5E7EB',
    borderTopColor: '#7C3AED',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: { color: '#6B7280', fontSize: '16px' },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: '12px',
    textAlign: 'center',
  },
  errorIcon: { fontSize: '48px' },
  retryBtn: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  kpiCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    position: 'relative',
    overflow: 'hidden',
  },
  kpiIconWrapper: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiIcon: { fontSize: '20px' },
  kpiContent: { display: 'flex', flexDirection: 'column' },
  kpiValue: { fontSize: '28px', fontWeight: 700, color: '#1E1B4B', lineHeight: 1 },
  kpiLabel: { fontSize: '13px', color: '#6B7280', marginTop: '4px' },
  kpiTrend: { marginTop: 'auto' },
  kpiTrendBadge: {
    fontSize: '11px',
    fontWeight: 600,
    padding: '4px 8px',
    borderRadius: '6px',
    background: '#F3F4F6',
    color: '#4B5563',
  },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '24px',
  },
  chartCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  },
  chartTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1E1B4B',
    marginBottom: '16px',
  },
  chartContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  chartLegend: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '3px',
  },
  legendLabel: { fontSize: '13px', color: '#4B5563', flex: 1 },
  legendValue: { fontSize: '14px', fontWeight: 600, color: '#1E1B4B' },
  tableSection: {
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #E5E7EB',
  },
  tableTitle: { fontSize: '18px', fontWeight: 600, color: '#1E1B4B', margin: 0 },
  tableCount: { fontSize: '13px', color: '#6B7280' },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 20px',
    color: '#6B7280',
  },
  emptyIcon: { fontSize: '48px', marginBottom: '12px', opacity: 0.5 },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    padding: '14px 20px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    background: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
  },
  tr: {
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  td: {
    padding: '16px 20px',
    borderBottom: '1px solid #F3F4F6',
    verticalAlign: 'middle',
  },
  employeeCell: { display: 'flex', alignItems: 'center', gap: '12px' },
  employeeAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '15px',
  },
  employeeName: { fontWeight: 600, color: '#1E1B4B', fontSize: '14px' },
  employeeEmail: { fontSize: '12px', color: '#6B7280' },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  statusDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
  },
  cubicleTag: {
    fontSize: '13px',
    color: '#7C3AED',
    fontWeight: 500,
  },
  postureCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  postureScore: { fontSize: '16px', fontWeight: 700, minWidth: '32px' },
  postureBar: {
    width: '60px',
    height: '6px',
    background: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  postureBarFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.5s ease',
  },
  sedentaryValue: { fontSize: '14px', fontWeight: 600, color: '#4B5563' },
  alertBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '28px',
    height: '28px',
    borderRadius: '8px',
    background: '#FEF3C7',
    color: '#D97706',
    fontSize: '13px',
    fontWeight: 600,
  },
  viewBtn: {
    padding: '8px 14px',
    background: 'transparent',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#7C3AED',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};
